import {
  Component,
  EventEmitter,
  OnInit,
  Output,
  HostListener,
  Renderer2,
  ElementRef,
  forwardRef,
  Inject,
  ChangeDetectorRef,
  PLATFORM_ID,
  NgZone,
} from '@angular/core';
import { Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { LocalStorageService } from '../local-storage.service';
import { TranslateService } from '@ngx-translate/core';
import { CognitoService } from '../service/cognito.service';
import { ThemeService } from '../service/theme.service';
import { ModalService } from '../service/device-modal.service';
import { environment } from '../environments/environment';
import { NavigationService } from '../service/navigation.service';
import { NotificationService } from '../service/notification.service';
import { RoleService } from '../service/role.service';
import {
  BaseMessage,
  WebSocketService,
  WebSocketSubscription,
} from '../service/websocket.service';
import { chatMessage, ChatService } from '../service/chat.service';
import { UserInactivityService } from '../service/user-inactivity.service';
import { filter, pairwise } from 'rxjs';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.css'],
  standalone: false,
})
export class HeaderComponent implements OnInit {
  // Declaration of component properties
  @Output() sideNavToggled = new EventEmitter<boolean>(); // Event emitter for toggling the side nav
  @Output() showSidebar = new EventEmitter<boolean>(); // Event emitter for showing the sidebar
  @Output() mode = new EventEmitter<boolean>(); // Event emitter for selecting a mode

  public userName: any = ''; // Variable for storing the user's name
  isSwitchOn: boolean = false; // Flag for switch status
  isDarkMode: boolean = false; // Flag for dark mode
  menuStatus: boolean = false; // Flag for menu status
  showHeader: boolean = false; // Flag for showing the header
  public getScreenWidth: any; // Variable for storing the screen width
  public screenWidthDeadLine: any = 768; // Threshold for small screen width
  public getScreenHeight: any; // Variable for storing the screen height
  smallscreen: boolean = false; // Flag for small screen
  userid: string = 'a'; // Variable for storing the user id
  currentUser: any = [];
  public displayNotificationList: boolean = false; //Notifications dialog box
  public authority: any = environment.users;
  public userInactive: boolean = false;
  public dropdownOpen = false;
  public selectedStatus: string; // Default status
  public statusLock: string = '';
  private wsChatUpdates: WebSocketSubscription = {
    subscriber: 'header-component',
    messageType: 'USER_STATUS_CHANGE_SYNC',
    callback: async (msg) => {
      const newStatus = msg.CONTENT['STATUS'];
      this.setStatus(newStatus, true); // set status updates
    },
  };

  private keepaliveTimer: ReturnType<typeof setInterval> | null = null;

  constructor(
    private router: Router,
    private localStorageService: LocalStorageService,
    private renderer: Renderer2,
    private elRef: ElementRef, // Inject ElementRef here without @Inject
    public cognitoService: CognitoService,
    public chatService: ChatService,
    @Inject(forwardRef(() => TranslateService))
    @Inject(forwardRef(() => TranslateService))
    private translate: TranslateService,
    public theme: ThemeService,
    public modal: ModalService,
    public nav: NavigationService,
    public notificationService: NotificationService,
    private changeDetectorRef: ChangeDetectorRef,
    public webSocketService: WebSocketService,
    public roleService: RoleService,
    public inactivityService: UserInactivityService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private ngZone: NgZone,
  ) {
    this.selectedStatus = this.inactivityService.selectedStatus;
  }

  // Lifecycle hook called when the component is initialized
  async ngOnInit() {
    // Check if the current page is the login page and set the theme to light if it is
    this.IsLoginPageActive();

    // Determine whether to show the header based on the current route
    this.showHeader = !this.router.url.includes('/login');

    // Get the current screen width and height
    this.getScreenWidth = window.innerWidth;
    this.getScreenHeight = window.innerHeight;

    // Initialize small screen state
    this.smallscreen = false;

    // Check and adjust the screen width state
    this.checkScreenWidth();

    //Subscription for user status
    this.inactivityService.getUserInactiveStatus().subscribe((status) => {
      this.userInactive = status;
      //Handles inactive status trigered by inactivity
      if (
        this.userInactive &&
        !['_away', '_busy', '_offline'].includes(this.statusLock)
      ) {
        this.selectedStatus = '_away';
      }
      //Handles inactive status set by user
      else if (
        this.userInactive &&
        ['_away', '_busy', '_offline'].includes(this.statusLock)
      ) {
        this.selectedStatus = this.statusLock;
      }
      // Defaulting to available
      else {
        this.selectedStatus = '_available';
      }
    });

    // Separate subscription for the transition from true → false (inactive to active)
    this.inactivityService
      .getUserInactiveStatus()
      .pipe(
        pairwise(),
        filter(([prev, curr]) => prev === true && curr === false),
      )
      .subscribe(() => {
        const validStatuses = this.inactivityService.statuses;

        if (this.statusLock && validStatuses.includes(this.statusLock)) {
          this.selectedStatus = this.statusLock;
        }
      });
    this.theme.getThemeClass();
    this.webSocketService.subscribe(this.wsChatUpdates);

    this.theme.getThemeClass();

    //Sustain user session
    if (isPlatformBrowser(this.platformId)) {
      console.log('session interval initiated');
      this.cognitoService.startSessionSustain(); // idempotent; won’t stack
    }

    await this.webSocketService.init();
    await this.chatService.init();

    //Manage Chat states
    if (isPlatformBrowser(this.platformId)) {
      this.startRealtimeKeepalive(); // idempotent; won’t stack
    }
  }

  ngOnDestroy() {
    //Chat State Interval
    this.stopRealtimeKeepalive();

    //Session Sustain Interval
    this.cognitoService.stopSessionSustain();
  }

  //Lifecycle hook called when a component has been initialized and all page elements have been checked
  ngAfterViewChecked(): void {
    //Called after every check of the component's view. Applies to components only.
    this.getUsername();
    this.changeDetectorRef.detectChanges(); // Prevents angular error
  }

  /** Idempotent keepalive for WebSocket + Chat init */
  startRealtimeKeepalive(intervalMs = 10_000): void {
    if (this.keepaliveTimer || !isPlatformBrowser(this.platformId)) return;

    const tick = () => {
      try {
        if (!this.webSocketService.checkWState()) {
          this.webSocketService.init();
        }
        if (!this.chatService.chatLoaded) {
          this.chatService.init();
        }
      } catch (err) {
        console.error('Realtime keepalive tick error:', err);
      }
    };

    // run once immediately
    tick();

    this.ngZone.runOutsideAngular(() => {
      this.keepaliveTimer = setInterval(tick, intervalMs);
    });
  }

  stopRealtimeKeepalive(): void {
    if (this.keepaliveTimer) {
      clearInterval(this.keepaliveTimer);
      this.keepaliveTimer = null;
    }
  }

  // Method for getting the current username
  getUsername() {
    try {
      this.userName = JSON.parse(this.localStorageService.getItem('user'));
    } catch (error) {
      this.userName = this.localStorageService.getItem('user');
    }
  }

  // Method for adjusting the toggle status
  adjustToggleStatus() {
    // Get a reference to the slider element
    const slider = document.querySelector('.slider');

    // Retrieve the saved theme status from local storage
    this.theme.themeStatus = this.localStorageService.getItem('theme');

    // Check if the theme status is set to 'dark'
    if (this.theme.themeStatus == 'dark') {
      // Add the 'checked' class to the slider to indicate it's on (dark theme)
      this.renderer.addClass(slider, 'checked');
    } else {
      // Remove the 'checked' class from the slider to indicate it's off (light theme)
      this.renderer.removeClass(slider, 'checked');
    }
  }

  // Method for toggling the dark mode
  toggleDarkMode(event: Event) {
    // Toggle the switch status
    this.theme.isSwitchOn = !this.theme.isSwitchOn;

    // Get the target element of the event as an input element
    const target = event.target as HTMLInputElement;

    // Update the dark mode status based on the switch's checked state
    this.theme.isDarkMode = target.checked;

    // Apply dark mode styling to the body and update theme status in local storage
    if (this.theme.isDarkMode) {
      // Add the 'dark-mode' class to the body to enable dark mode
      document.body.classList.add('dark-mode');

      // Update theme status and save it in local storage
      this.theme.themeStatus = 'dark';
      this.localStorageService.addItem('theme', this.theme.themeStatus);
      console.log('theme: ' + this.localStorageService.getItem('theme'));
    } else {
      // Remove the 'dark-mode' class from the body to disable dark mode
      document.body.classList.remove('dark-mode');

      // Update theme status and save it in local storage
      this.theme.themeStatus = 'light';
      this.localStorageService.addItem('theme', this.theme.themeStatus);
    }
  }

  // Event listener for window resize
  @HostListener('window:resize')
  onWindowResize() {
    // Update the screen width and height based on the resized window
    this.getScreenWidth = window.innerWidth;
    this.getScreenHeight = window.innerHeight;
    // Check and adjust the screen width state
    this.checkScreenWidth();
  }

  // Method for checking the screen width
  checkScreenWidth() {
    // Compare the current screen width with the defined threshold
    if (this.getScreenWidth <= this.screenWidthDeadLine) {
      // If the screen width is below or equal to the threshold, set smallscreen state to true
      this.smallscreen = true;
    } else {
      // If the screen width is above the threshold, set smallscreen state to false
      this.smallscreen = false;
    }
  }

  // Method for toggling the side nav
  SideNavToggle() {
    // Toggle the menuStatus state to show/hide the side nav
    this.menuStatus = !this.menuStatus;

    // Emit the updated menuStatus using the sideNavToggled event
    this.sideNavToggled.emit(this.menuStatus);
  }

  // Method for showing the sidebar
  ShowSidebar() {
    // Emit the smallscreen state to show the sidebar based on the current screen width
    this.showSidebar.emit(this.smallscreen);
  }

  // Method for checking if the login page is active
  IsLoginPageActive() {
    // Get the current URL of the page
    const url = window.location.href;

    // Set the switch status to off
    this.theme.isSwitchOn = false;

    // Return true if the URL includes 'login', otherwise return false
    return url.includes('login');
  }

  // Method for clearing the local storage
  async clearStorage() {
    let theme = localStorage.getItem('theme') ?? 'light';
    let language = localStorage.getItem('language') ?? 'fr';
    theme = theme?.replaceAll(/["\\]/g, '') ?? 'light';
    // Clear the local storage
    this.roleService.roles = [];
    localStorage.clear();
    sessionStorage.clear();
    localStorage.setItem('theme', theme ?? 'light');
    localStorage.setItem('language', language);

    // Send out logout notification *before* performing logout
    await this.webSocketService.sendLogOutNotif();
    try {
      // Attempt to sign the user out using cognitoService
      await this.cognitoService
        .signOut()
        .then(async () => {
          this.webSocketService.cleanUp();
          this.chatService.cleanUp();
        })
        .catch((error) => {
          console.error('Sign out failed', error);
        });
      this.chatService.isChatModalOpen = false;
      this.webSocketService.close();
    } catch (error) {
      // If an error occurs during sign-out, log the error and navigate to the login page
      console.error('Error signout out:', error);
    } finally {
      // Temporary fix to force the app to be destroyed
      // Otherwise, services will still hold data even after login out
      window.location.href = '/login';
    }
  }

  // Method for showing the notifications dropdown
  showNotificationsDropdown() {
    // Toggle the notifications property
    this.displayNotificationList = !this.displayNotificationList;

    // TODO We might no want to mark them all as seen, TBD
    this.notificationService.markAllNewNotificationsAsSeen();
  }

  @HostListener('document:click', ['$event'])
  handleClick(event: Event): void {
    const target = event.target as HTMLElement;

    // If the click is outside the notification dropdown box and bell icon, close the box
    if (!this.isNotificationElement(target)) {
      this.displayNotificationList = false;
    }
  }

  private isNotificationElement(target: HTMLElement): boolean {
    // Check if the click is within the notification dropdown box, bell icon or notification count
    return target.closest('.notificationElement') !== null;
  }

  async checkAuth(role: string, type: string) {
    //Validate role and type. Sets cognitoService.userAllowed as a boolean value
    await this.cognitoService.getCurrentRole([role], [type], false); //Role Check
  }

  //Validates list items in the hamburger navigation menu
  validateListItem(role: string, type: string): boolean {
    this.checkAuth(role, type);

    let allowed = this.cognitoService.userAllowed;

    return allowed;
  }

  isDashboard() {
    // Get the complete URL of the current page
    const url = window.location.href;

    // Check if the URL includes the string 'dashboard'
    // This indicates whether the current page is the login page
    return url.includes('dashboard');
  }

  /**
   * Function that opens the chat
   */
  openChat() {
    this.chatService.isChatModalOpen = true;
    this.chatService.toggleChat();
    // Get the currently open conversation ID from the chat service
    const openedConversation =
      this.chatService.currentOpenConversation ?? undefined;
    // If there is an active conversation, dispatch event
    if (openedConversation) {
      const event = new CustomEvent('chatOpened', {
        detail: { conversation: openedConversation },
      });
      // Dispatch the event so other components can listen and respond
      window.dispatchEvent(event);
    }
  }
  toggleStatusDropdown() {
    this.dropdownOpen = !this.dropdownOpen;
  }
  /**
   * Updates the user's status and optionally notifies other tabs via WebSocket.
   *
   * @param status - The new status string (e.g. '_available', '_away', '_offline', '_busy')
   * @param sync - Optional boolean flag. If false (default), triggers a WebSocket sync message.
   */
  setStatus(status: string, sync: boolean = false) {
    status = status;
    this.selectedStatus = status;
    this.dropdownOpen = false;
    switch (status) {
      case '_available':
        this.inactivityService.setUserAvailable();
        break;
      case '_away':
        this.inactivityService.setUserAway();
        break;
      case '_busy':
        this.inactivityService.setUserBusy();
        break;
      case '_offline':
        this.inactivityService.setUserOffline();
        break;
    }
    this.statusLock = status;

    //Safely exit if called for status sync
    if (sync) {
      return;
    }
    //Update user contacts of status change
    let wsStatusUpdate = {
      DISPATCH: 'CHAT',
      ACTION: 'USER_STATUS_CHANGE',
      CONTENT: {
        TARGETS: this.chatService.contactListSubs,
        USER: this.chatService.userSub,
        STATUS: this.statusLock,
      },
    };
    this.webSocketService.send(wsStatusUpdate);
  }
}
